/**
 * Returns whether `clientX` / `clientY` lie within `el`'s border box in viewport
 * coordinates (same space as `getBoundingClientRect()`).
 */
function isClientXYInsideElement(
  clientX: number,
  clientY: number,
  el: HTMLElement
): boolean {
  const r = el.getBoundingClientRect();
  return (
    clientX >= r.left &&
    clientX <= r.right &&
    clientY >= r.top &&
    clientY <= r.bottom
  );
}

/**
 * `TouchEvent` does not expose `clientX` / `clientY` on the event; they live on
 * `Touch` entries (e.g. `changedTouches` for `touchstart`).
 */
function getClientCoordinatesForPointerHitTest(
  event: MouseEvent | TouchEvent
): { readonly clientX: number; readonly clientY: number } | undefined {
  if (typeof TouchEvent !== 'undefined' && event instanceof TouchEvent) {
    const touch =
      event.changedTouches[0] ?? event.targetTouches[0] ?? event.touches[0];
    if (touch === undefined) {
      return undefined;
    }
    return { clientX: touch.clientX, clientY: touch.clientY };
  }

  if (event instanceof MouseEvent) {
    return { clientX: event.clientX, clientY: event.clientY };
  }

  return undefined;
}

/**
 * Returns whether a pointer event should be treated as occurring "inside" `root`
 * for outside-click detection.
 *
 * Accepts **`MouseEvent`** (including `PointerEvent`) or **`TouchEvent`** (e.g. document
 * `touchstart` listeners for outside-click handling).
 *
 * Uses `composedPath()` so interactions inside **Shadow DOM** (e.g. Modus web components)
 * count as inside. Also matches by **`data-testid`** on nodes in the path: after a
 * Solid **Portal** update, `root` refs can briefly point at a **detached** node, so
 * `path.includes(root)` may be false even when the user clicked the visible UI. Comparing
 * `dataset.testid` avoids that.
 *
 * As a last resort (rare cases where the path is still wrong), the event coordinates
 * are tested against `root.getBoundingClientRect()`.
 *
 * **Recommended:** set a stable `data-testid` on `root` whenever this helper is used.
 * If you also need to treat clicks on a separate element (e.g. an anchor) as inside,
 * call this function again for that element.
 */
export function isPointerEventInsideRoot(
  event: MouseEvent | TouchEvent,
  root: HTMLElement
): boolean {
  const path = event.composedPath();

  if (path.includes(root)) {
    return true;
  }

  const testId = root.dataset['testid'];
  if (testId) {
    for (const node of path) {
      if (node instanceof HTMLElement && node.dataset['testid'] === testId) {
        return true;
      }
    }
  }

  const coords = getClientCoordinatesForPointerHitTest(event);
  if (coords === undefined) {
    return false;
  }

  return isClientXYInsideElement(coords.clientX, coords.clientY, root);
}
